import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import client from '../../../client';
import { useMessage } from 'app/shared-components/MessageUtils';
import { useTranslation } from 'react-i18next';
import { threeDecimalformatCurrency } from '../../../utils/numberFormatting';

const ModalUpdateBatch = ({ isOpen, setIsOpen, selectedBatch, onUpdated }) => {
  const { t } = useTranslation('navigation');
  const [price, setPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const showMessage = useMessage();
  const inputRef = useRef(null);

  useEffect(() => {
    if (selectedBatch && isOpen) {
      setPrice(selectedBatch.price_per_unit?.toString() || '');
      setError('');
    }
  }, [selectedBatch, isOpen]);

  // Force focus every time modal opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (inputRef.current) {
          const inputElement = inputRef.current.querySelector('input');
          if (inputElement) {
            inputElement.focus();
          }
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setPrice('');
    setError('');
  };

  const handleUpdate = async () => {
    if (!price || isNaN(parseFloat(price)) || parseFloat(price) < 0) {
      setError(t('INVALID_PRICE'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get compacctnum from localStorage
      let compacctnum = '';
      try {
        const lSData = JSON.parse(localStorage.getItem('persist:root'));
        const userData = JSON.parse(lSData.loadz_auth).AuthReducer.user;
        compacctnum = userData.company.compacctnum;
      } catch (e) {
        compacctnum = '';
      }

      const response = await client.put(`/fuel-inventory/update-batch/${selectedBatch.batch_id}`, {
        price_per_unit: parseFloat(price),
        compacctnum: compacctnum
      });

      if (response && response.data) {
        showMessage(t('BATCH_UPDATED_SUCCESSFULLY'), 'success');
        onUpdated();
        handleClose();
      }
    } catch (error) {
      console.error('Error updating fuel price:', error);
      setError(error.response?.data?.message || t('ERROR_UPDATING_BATCH'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        <Typography variant="h6" component="div">
          {t('UPDATE_BATCH_DATA')}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {selectedBatch && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('BATCH_ID')}: {selectedBatch.batch_id}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('PRODUCT_NAME')}: {selectedBatch.product_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('CURRENT_PRICE')}: ${threeDecimalformatCurrency(selectedBatch.price_per_unit)}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'black', 
                fontWeight: '400',
                mb: 0,
                fontSize: '1.3rem'
              }}
            >
              {t('PRICE_PER_UNIT')}
            </Typography>
            <TextField
              ref={inputRef}
              fullWidth
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              error={!!error}
              helperText={error}
              onClick={(e) => {
                e.target.focus();
                e.target.select();
              }}
              inputProps={{
                min: 0,
                step: 0.001,
                style: {
                  paddingTop: '7px',
                  paddingBottom: '7px'
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '1.3rem'
                },
                '& .MuiInputBase-input': {
                  cursor: 'text'
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          {t('CANCEL')}
        </Button>
        <Button 
          onClick={handleUpdate}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t('UPDATING') : t('UPDATE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalUpdateBatch;
